/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useOutletContext } from "react-router-dom";
import { errorCodeTranslation } from "../../../../Apis/ErrorCode";
import { errorMsg, getDateFormat } from "../../../../Apis/functions";
import { getAppetizer } from "../../../../reduxToolkit/FoodieOrders/FoodieOrdersApi";
import {
    GetAllCafeterias,
    GetAllCafeteriasByUserId,
} from "../../../../reduxToolkit/FoodieShift/FoodieShiftApi";
import { DishCategoryPayload } from "../../Dining/Menu/Model/Model";
import {GetFirstFoodieSetting} from '../../../../reduxToolkit/Foodie/FoodieApi';


const SelectionMealsApp = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation()
    const { meal, setMeal } = useOutletContext()
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { allCafeterias } = useSelector(state => state.FoodieShiftSlice)
    const [dishes, setDishes] = useState([])
    const [emptyFields, setEmptyFields] = useState(false);
    const [codeMessage, setCodeMessage] = useState('')
    const prevMealRef = useRef({ cafeteriaId: "", date: "", mealTime: "" });
    const hasFetchedDishes = useRef(false);
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);
    const { getUserFullInformation } = useSelector((state) => state.CommonsSlice);
    const cafeteriasOptions = allCafeterias?.map((item) => ({ id: item.id, name: item.name })) || [];
    const location = useLocation();
    let { state } = location; // Get state from location
    let foodieRequestId = state?.foodieRequestId;
    const [showBanner, setShowBanner] = useState(true);



    const handleSelection = (id) => {
        setMeal((prevMeal) => {
            const alreadySelected = prevMeal.menuMealTimeIds.includes(id);

            //update ids
            const updatedIds = alreadySelected
                ? prevMeal.menuMealTimeIds.filter(item => item !== id)
                : [...prevMeal.menuMealTimeIds, id];

            // Call helper to update menuMealTimesObj
            const updatedObj = updateMenuMealTimesObj(prevMeal, id, dishes, alreadySelected);

            return {
                ...prevMeal,
                menuMealTimeIds: updatedIds,
                menuMealTimesObj: updatedObj
            };
        });
    };



    /**
     * Updates menuMealTimesObj with the selected dish id.
     *
     * @param {Object} prevMeal - Current meal state
     * @param {string} id - Selected dish id
     * @param {Array} dishes - List of course groups with meals
     * @param {boolean} alreadySelected - If the dish was already selected
     * @returns {Object} Updated menuMealTimesObj
     */
    function updateMenuMealTimesObj(prevMeal, id, dishes, alreadySelected) {
        // Find which courseType this dish belongs to
        let foodieCourseTypeId = null;
        for (const group of dishes) {
            if (group.meals.some(meal => meal.id === id)) {
                foodieCourseTypeId = group.foodieCourseTypeId;
                break;
            }
        }

        if (!foodieCourseTypeId) {
            return prevMeal.menuMealTimesObj;
        }

        // Map courseTypeId to category
        const category = DishCategoryPayload[foodieCourseTypeId];

        return {
            ...prevMeal.menuMealTimesObj,
            [category]: alreadySelected ? null : { id }
        };
    }

    const fetchDishes = async () => {
        const body = {
            "foodieCafeteriaId": meal.cafeteriaId,
            "foodieMealTimeId": meal.mealTime,
            "key": "",
            "servingDate": new Date(meal.date).getTime(),
            "userId": getUserFullInformation?.id || userId,
            "isExtra": foodieRequestId ? true : null
        }

        try {
            const response = await dispatch(getAppetizer({ ...body })).unwrap(); //unwrap() to receive directly payload with try catch with dispatch(thunk)
            hasFetchedDishes.current = true;
            const dishesData = response?.data || [];
            setDishes(dishesData);
            setMeal((prevData) => ({
                ...prevData,
                //setting serving date from the first dish
                servingDate: dishesData[0]?.meals[0]?.servingDate
            }))
        } catch (error) {
            const errorData = error?.response?.data || error;
            const formattedDate = getDateFormat(meal?.date, false)
            errorMsg(errorData?.code, { day: formattedDate })
            setCodeMessage(t(errorCodeTranslation[errorData?.code]) || t('something_went_wrong_2'))
            setDishes([])
        }

    }

    /**
     * Get foodie setting for cost
     */
    useEffect(() => {
        dispatch(GetFirstFoodieSetting());
    }, []);


    /**
     * Determines which action should be dispatched:
     *    - If a foodieRequestId exists, the full list of cafeterias is retrieved.
     *    - If it does not exist, only the cafeterias that belong to the user are retrieved.
     */
    useEffect(() => {
        const allCafeteriasAction = foodieRequestId ? GetAllCafeterias : GetAllCafeteriasByUserId;

        dispatch(allCafeteriasAction());

    }, [dispatch])


    //get Dishes first time
    useEffect(() => {
        if (meal.isValidMealTimeModal()) {
            fetchDishes();
        }
    }, [meal.cafeteriaId, meal.date, meal.mealTime]);


    // Clear menuMealTimeIds if key fields change and dishes have already been loaded
    useEffect(() => {
        const { cafeteriaId, date, mealTime } = meal;
        const prev = prevMealRef.current;

        const changed =
            prev.cafeteriaId !== cafeteriaId ||
            prev.date !== date ||
            prev.mealTime !== mealTime;

        if (changed && hasFetchedDishes.current) {
            setMeal((prevState) => ({
                ...prevState,
                menuMealTimeIds: [],
            }));
        }

        prevMealRef.current = { cafeteriaId, date, mealTime };
    }, [meal, setMeal]);


    useEffect(() => {
        if (meal?.isValidMealTimeModal?.()) {
            setEmptyFields(false);
        } else {
            setEmptyFields(true);
        }
    }, [meal]);


    return {
        t,
        DEV,
        permission,
        cafeteriasOptions,
        dishes,
        emptyFields,
        codeMessage,
        handleSelection,
        showBanner, setShowBanner
    }

}


export default SelectionMealsApp
